import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { ProductQuoteRequestItem, ProductQuoteResult } from '@typesApp/ProductQuote'
import { useLazyGetClaimRecordNoCacheQuery, useLazyGetProductQuoteQuery } from '@redux/queries/claimRecords'
import {
  setProductQuoteLoading,
  setProductQuoteError,
  setProductQuoteSuccess,
} from '@redux/slices/insuranceMemberSlice'
import { useClaimRecord } from '@hooks/useClaimRecord'
import { getServiceStatus } from '../helpers/getServiceStatus'
import { CommerceProductQuoteRequestItem } from '../types'
import { isContactLensProductType, isFrameProductType } from '@utils/productTypeUtil'

const EMPTY_RESULT: ProductQuoteResult = {
  productQuotes: [],
}

type UseProductQuoteReturn = {
  isLoading: boolean
  isError: boolean
  requestQuote: (request: CommerceProductQuoteRequestItem[]) => Promise<ProductQuoteResult>
}

export const useProductQuoteService = (): UseProductQuoteReturn => {
  const dispatch = useDispatch()
  const { claimRecordId } = useClaimRecord()
  const [fetchClaimRecordNoCache] = useLazyGetClaimRecordNoCacheQuery()
  const [triggerRequest, { isLoading, isError }] = useLazyGetProductQuoteQuery()

  const requestQuote = useCallback(
    async (request: CommerceProductQuoteRequestItem[]): Promise<ProductQuoteResult> => {
      dispatch(setProductQuoteLoading())

      if (!claimRecordId) {
        return EMPTY_RESULT
      }

      try {
        // always fetch latest claim record
        const claimRecord = await fetchClaimRecordNoCache(claimRecordId).unwrap()
        if (!claimRecord) {
          return EMPTY_RESULT
        }

        const status = getServiceStatus('product-quote', { claimRecord, claimRecordId, claimRecordDeleted: false })
        if (status !== 'ready') {
          return EMPTY_RESULT
        }

        const products = mapCommerceProducts(request)
        const result = await triggerRequest({ claimRecordId, products }).unwrap()
        dispatch(setProductQuoteSuccess(result))
        return result
      } catch (error) {
        dispatch(setProductQuoteError(true))
        return { ...EMPTY_RESULT, isError: true }
      }
    },
    [dispatch, triggerRequest, fetchClaimRecordNoCache, claimRecordId]
  )

  return {
    isLoading,
    isError,
    requestQuote,
  }
}

const mapCommerceProducts = (products: CommerceProductQuoteRequestItem[]): ProductQuoteRequestItem[] => {
  return (products ?? []).map(product => {
    const {
      productType,
      quantity = 1,
      unitFullPrice,
      salePrice,
      retailPrice,
      serviceCategories,
      productIdentifier,
      productName,
      eye,
      groupId,
    } = product

    const isFrame = isFrameProductType(productType)
    const isContacts = isContactLensProductType(productType)

    const result: ProductQuoteRequestItem = {
      productType,
      productName,
      ecommerceId: productIdentifier,
      ecommerceIdType: 'RXC_ID',
      quantity,
      unitFullPrice,
      salePrice,
      retailPrice,
      eye,
      groupId,
      serviceCategories: Array.isArray(serviceCategories) && serviceCategories.length ? serviceCategories : undefined,
    }

    if (!isContacts) {
      delete result.eye
      delete result.groupId

      if (!isFrame) {
        delete result.productName
      }
    }

    return result
  })
}
